// Astrid = AI teammate & orchestration layer. Data-driven multi-scenario hero.
export const CORE_POS = [0, 0, -2.4];

export const COLORS = {
  reason: '#5b9dff', context: '#35c4f0', service: '#8b93ff',
  insight: '#f6a723', sent: '#37d39b', brand: '#ff5c5c', dim: '#33405e',
};

export const CLUSTERS = {
  organisation:{ pos: [-2.4, 1.6, -3.6], color: COLORS.reason },
  properties:  { pos: [2.5, 1.9, -3.9],  color: COLORS.reason },
  accounts:    { pos: [3.1, -0.5, -4.3], color: COLORS.context },
  bills:       { pos: [-3.1, -0.6, -4.1],color: COLORS.context },
  usage:       { pos: [-1.7, -2.0, -4.7],color: COLORS.context },
  rates:       { pos: [1.9, -2.0, -4.7], color: COLORS.context },
  historical:  { pos: [0.2, 2.6, -4.9],  color: COLORS.reason },
  vendors:     { pos: [2.6, 1.2, -3.7],  color: COLORS.reason },
  contacts:    { pos: [3.2, -0.9, -4.5], color: COLORS.context },
  ach:         { pos: [-2.6, -0.4, -3.9],color: COLORS.service },
  outreach:    { pos: [0.0, -2.3, -6.6], color: COLORS.service },
  missing:     { pos: [-2.5, 1.4, -3.7], color: COLORS.reason },
  reporting:   { pos: [3.5, 0.6, -6.7],  color: COLORS.service },
  team:        { pos: [-3.5, 0.8, -6.7], color: COLORS.insight },
  approval:    { pos: [0.2, 2.3, -7.0],  color: COLORS.service },
};

const pos = (k) => CLUSTERS[k].pos;

// Four rotating responsibilities sharing one intelligence layer.
export const SCENARIOS = [
  {
    tag: 'Ask', intent: 'Why did our electricity spend increase this month?',
    status: 'Understanding your portfolio & reasoning across the data',
    clusters: ['organisation', 'properties', 'accounts', 'bills', 'usage', 'rates', 'historical'],
    fragments: [['42 properties', 'context'], ['126 electricity accounts', 'context'], ['Usage +11.8%', 'insight'], ['Avg rate +6.2%', 'insight']],
    card: {
      type: 'insight', title: 'Electricity spend increased 18.4% this month.',
      bullets: ['11.8% from higher consumption', '6.2% from higher average rates', '3 sites account for 61% of the variance'],
      cta: 'Investigate those 3 sites?',
    },
  },
  {
    tag: 'Create', intent: 'Set up missing bill tracking across the portfolio.',
    status: 'Building the setup from your organisation & accounts',
    clusters: ['organisation', 'properties', 'accounts', 'bills', 'missing'],
    fragments: [['612 common-area accounts', 'context'], ['23 unit accounts', 'context'], ['= 635 accounts', 'insight']],
    card: {
      type: 'setup', title: 'Missing Bill Setup', metric: '635', metricLabel: 'accounts prepared',
      rows: ['Organisation default available', 'Account-level exceptions supported'],
      line: "I've prepared the portfolio. Add any exceptions, or I can apply your organisation default.",
      buttons: ['Review Setup', 'Apply'],
    },
  },
  {
    tag: 'Communicate', intent: 'Start ACH migration with all 100 vendors.',
    status: 'Matching vendors, contacts & preparing outreach in parallel',
    clusters: ['organisation', 'vendors', 'accounts', 'contacts', 'ach', 'outreach'],
    actClusters: ['outreach', 'approval'],
    fragments: [['100 vendors identified', 'context'], ['83 vendor contacts', 'context'], ['17 need details', 'insight']],
    card: {
      type: 'approval', line: 'I matched every vendor and prepared a personalised ACH request for each — nothing is sent automatically.',
      stats: [['100', 'requests prepared'], ['83', 'ready to send'], ['17', 'need contact info']],
      buttons: ['Review Requests', 'Approve & Send'],
    },
    done: {
      type: 'completion', title: 'Done — 83 ACH setup requests sent.',
      line: "I'll keep vendor replies, follow-ups and onboarding status connected to each vendor and bring exceptions back to your team.",
    },
  },
  {
    tag: 'Collaborate', intent: 'Prepare the ACH migration and get Sarah & James to review the exceptions.',
    status: 'Preparing the migration & assigning exception work to your team',
    clusters: ['vendors', 'accounts', 'ach', 'team', 'approval'],
    fragments: [['100 vendors', 'context'], ['83 ready', 'context'], ['17 exceptions', 'insight']],
    card: {
      type: 'team', title: 'Migration workspace ready',
      line: "I've prepared the migration and assigned the exceptions — Astrid and your team, working together.",
      people: [['Sarah', '9 exceptions'], ['James', '8 exceptions']],
      summary: '100 vendors prepared · 17 exceptions assigned · 2 reviewers',
    },
  },
];

const PHASES = { // id: dur(ms)
  ask: 1700, dive: 1200, think: 2600, act: 1900, present: 3600, execute: 2000, done: 3200, hold: 1000,
};
const DEPTH = { ask: 3.6, dive: 1.6, think: 0.2, act: -0.3, present: 3.5, execute: 0.5, done: 3.5, hold: 3.5 };
const BEHIND_IDS = new Set(['dive', 'think', 'act', 'execute']);
export const FOCUSED_IDS = new Set(['ask', 'present', 'done', 'hold']);

export const TIMELINE = [];
SCENARIOS.forEach((s, si) => {
  const ids = s.card.type === 'approval'
    ? ['ask', 'dive', 'think', 'act', 'present', 'execute', 'done', 'hold']
    : ['ask', 'dive', 'think', 'present', 'hold'];
  ids.forEach((id) => TIMELINE.push({ scn: si, id, dur: PHASES[id] }));
});

export function getStoryState(scn, id) {
  const s = SCENARIOS[scn];
  let clusters = [];
  let flows = [];
  let fragments = [];
  if (id === 'think') {
    clusters = s.clusters;
    fragments = s.fragments.map(([text, tone], i) => ({ pos: [CLUSTERS[s.clusters[i % s.clusters.length]].pos[0], CLUSTERS[s.clusters[i % s.clusters.length]].pos[1] + 0.5, CLUSTERS[s.clusters[i % s.clusters.length]].pos[2]], text, tone }));
    flows = s.clusters.slice(0, 4).map((k) => ({ from: pos(k), to: CORE_POS, color: COLORS.context, count: 3 }));
  } else if (id === 'act') {
    clusters = s.actClusters || s.clusters;
    flows = [{ from: CORE_POS, to: pos('outreach'), color: COLORS.service, count: 6 }];
  } else if (id === 'execute') {
    clusters = ['outreach'];
    flows = [{ from: CORE_POS, to: pos('outreach'), color: COLORS.sent, count: 8 }];
  }
  return { depth: DEPTH[id] ?? 3.5, clusters, flows, fragments, behind: BEHIND_IDS.has(id) };
}
